import { events, heatColor, surgeList, type Trend } from "@/mocks/guaData";

function TrendMark({ trend }: { trend: Trend }) {
  if (trend === "up") {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-600">
        <i className="ri-arrow-up-line text-[13px]"></i>
      </span>
    );
  }
  if (trend === "down") {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
        <i className="ri-arrow-down-line text-[13px]"></i>
      </span>
    );
  }
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-background-200 text-foreground-600">
      <i className="ri-subtract-line text-[13px]"></i>
    </span>
  );
}

export default function RankBoard() {
  const ranked = [...events].sort((a, b) => b.heat - a.heat).slice(0, 8);
  const maxHeat = ranked[0]?.heat ?? 1;

  return (
    <section id="rank" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-primary-600">
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-fire-line text-[13px]"></i>
              </span>
              HOT&nbsp;RANKING
            </p>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">
              今日热榜 <span className="text-primary-500">TOP 8</span>
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              谁在风口浪尖，数字最有发言权。热度值综合全网讨论量、阅读量与上升速度得出，供你快速判断当下最火事件。
            </p>
          </div>
          <span className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-xs text-secondary-900">
            <span className="heat-pulse inline-block h-2 w-2 rounded-full bg-primary-500"></span>
            滚动更新 · 最近刷新 22:18
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <ol className="flex flex-col gap-3 lg:col-span-2">
            {ranked.map((ev, idx) => (
              <li
                key={ev.id}
                className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-100/70 p-4 transition-colors hover:border-primary-200 hover:bg-background-100 md:p-5"
              >
                <span
                  className={`font-heading flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-xl ${
                    idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                  }`}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <a href="#feed" className="block">
                    <h3 className="text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-lg">
                      {ev.title}
                    </h3>
                  </a>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="whitespace-nowrap rounded bg-secondary-100 px-2 py-0.5 text-secondary-900">{ev.catLabel}</span>
                    <span className="whitespace-nowrap">{ev.time.replace("2026-09-0", "09-0")}</span>
                    <span className="whitespace-nowrap hidden sm:inline">{ev.tags.join(" · ")}</span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`font-heading text-2xl ${heatColor(ev.heat)}`}>{ev.heat}</span>
                    <TrendMark trend={ev.trend} />
                  </div>
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-primary-400"
                      style={{ width: `${(ev.heat / maxHeat) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className="ri-arrow-up-double-line text-[15px]"></i>
                  </span>
                  飙升最快
                </h3>
                <span className="whitespace-nowrap rounded-full bg-background-50 px-2.5 py-1 text-xs text-foreground-600">2 小时内</span>
              </div>
              <ul className="mt-4 flex flex-col divide-y divide-background-100">
                {surgeList.map((s) => (
                  <li key={s.title} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900">{s.title}</p>
                      <p className="text-xs text-foreground-500">{s.catLabel}</p>
                    </div>
                    <span className="flex items-center gap-1 whitespace-nowrap font-heading text-base text-primary-600">
                      <i className="ri-arrow-up-line text-[13px]"></i>
                      {s.delta}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-background-950 p-6 text-background-50">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                <i className="ri-message-3-line text-lg"></i>
              </div>
              <p className="mt-4 text-base leading-relaxed text-background-100">
                热度不等于真相，榜单只负责告诉你“大家都在聊什么”。至于聊得对不对，让我们一起多核实、少上头。
              </p>
              <p className="mt-4 text-sm font-semibold text-accent-400">—— 吃瓜榜单 编辑部</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}